/**
 * The error state of a thumbnail which does not load (a CDN which refuses a burst of
 * requests, a file which is gone): one more attempt after a short pause, then the tile says
 * so - an icon and a line of text over the muted box (eg-tile--error, components/tile.less)
 * instead of an empty box with working controls. Bound once on the document in the capture
 * phase, before the body is parsed, because an error event does not bubble; so every image
 * of the page counts, also one a script adds later.
 *
 * The attempt asks an address of the site with a marker (egretry=1, which the thumbnail
 * endpoint ignores) and the address of another host - Flickr, Google Photos, a bucket - as it
 * is: a parameter of ours has no business there and could change what that host answers.
 * The image has no address for a moment before it gets its addresses back, so every browser
 * asks again; Safari repeats the error of an address which is set once more unchanged.
 *
 * A cropped thumbnail outside a tile - the 50 px squares of the event module and the latest
 * images, the similar tags module - has no room for a line of text: after the same one attempt it shows a small
 * mark of its own, the image glyph on the muted surface in the box of the image
 * (eg-img-failed, eventgallery.less), instead of the broken-image glyph of the browser. The
 * image stays inside the mark, invisible but with its size and its alt, so the box and the name
 * of the link are kept.
 */
import {icon} from "../../common/js/icon";

const RETRY_DELAY = 1500;
const RETRY_MARKER = 'egretry';

export default class ThumbnailErrors {

    static bind(root = document) {
        root.addEventListener('error', (event) => ThumbnailErrors.onError(event), true);
    }

    static onError(event) {
        const image = event.target;
        if (!(image instanceof HTMLImageElement) || !image.classList.contains('eg-img')) {
            return;
        }
        const tile = image.closest('.eg-tile');
        if (!tile && !image.classList.contains('eg-img--crop')) {
            return;
        }
        if (image.dataset.egRetried !== '1') {
            image.dataset.egRetried = '1';
            setTimeout(() => ThumbnailErrors.retry(image), RETRY_DELAY);
            return;
        }
        if (tile) {
            ThumbnailErrors.markFailed(tile, image);
        } else {
            ThumbnailErrors.markBoxFailed(image);
        }
    }

    /**
     * Asks for the file once more: the addresses of the site with the marker, the others as
     * they are. The src goes before the srcset and both come back in the next task, so no
     * browser answers from its memory of the failed attempt and none asks for another size in
     * between.
     */
    static retry(image) {
        const srcset = image.getAttribute('srcset');
        const src = image.getAttribute('src');
        image.removeAttribute('src');
        image.removeAttribute('srcset');
        setTimeout(() => {
            if (srcset) {
                image.setAttribute('srcset', srcset.split(',').map((candidate) => {
                    const parts = candidate.trim().split(/\s+/);
                    parts[0] = ThumbnailErrors.withMarker(parts[0]);
                    return parts.join(' ');
                }).join(', '));
            }
            if (src) {
                image.setAttribute('src', ThumbnailErrors.withMarker(src));
            }
        }, 0);
    }

    /**
     * The address with the marker when it is an address of the site; the address of another
     * host unchanged.
     */
    static withMarker(url) {
        if (!url || url.includes(RETRY_MARKER + '=') || !ThumbnailErrors.isOfThisSite(url)) {
            return url;
        }
        return url + (url.includes('?') ? '&' : '?') + RETRY_MARKER + '=1';
    }

    static isOfThisSite(url) {
        try {
            return new URL(url, document.baseURI).origin === window.location.origin;
        } catch (e) {
            return false;
        }
    }

    static markFailed(tile, image) {
        if (tile.classList.contains('eg-tile--error')) {
            return;
        }
        tile.classList.add('eg-tile--error');

        const media = image.closest('.eg-tile__media') || image.parentElement;
        const texts = typeof window !== 'undefined' ? window.EventGalleryLightboxConfiguration : null;
        const text = texts && texts.KEY_IMAGE_LOAD_FAILED ? String(texts.KEY_IMAGE_LOAD_FAILED) : '';

        const note = document.createElement('span');
        note.className = 'eg-tile__error';
        note.innerHTML = icon('image', 'eg-icon--lg') + (text ? '<span class="eg-tile__error-text">' + ThumbnailErrors.escape(text) + '</span>' : '');
        if (media) {
            media.appendChild(note);
        }
    }

    /**
     * The mark of a cropped thumbnail outside a tile: the image goes into a span of the same box,
     * which draws the glyph over it; the text of the page is its tooltip.
     */
    static markBoxFailed(image) {
        const parent = image.parentElement;
        if (!parent || parent.classList.contains('eg-img-failed')) {
            return;
        }
        const texts = typeof window !== 'undefined' ? window.EventGalleryLightboxConfiguration : null;
        const mark = document.createElement('span');
        mark.className = 'eg-img-failed';
        if (texts && texts.KEY_IMAGE_LOAD_FAILED) {
            mark.title = String(texts.KEY_IMAGE_LOAD_FAILED);
        }
        parent.insertBefore(mark, image);
        mark.appendChild(image);
        mark.insertAdjacentHTML('beforeend', icon('image', 'eg-img-failed__icon'));
    }

    static escape(value) {
        return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
    }
}
